import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { sensitiveEnv } from "../../../config/sensitiveEnv";
import "../../../config/styles/theme.less";
import {
  type IMapComponentProps,
  type IMapComponentRef,
} from "../../interfaces/map.interface";
import MapErrorOverlay from "../MapErrorOverlay";
import { DEFAULT_MAP_COORDINATES, MAP_STYLE } from "./constants";
import "./style.less";

mapboxgl.accessToken = sensitiveEnv.mapboxToken;

const isValidCoordinate = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const MapComponent = forwardRef<IMapComponentRef, IMapComponentProps>(
  (props, ref) => {
    const { lat, lon, onLoad } = props;

    const safeLat = isValidCoordinate(lat) ? lat : DEFAULT_MAP_COORDINATES.lat;
    const safeLon = isValidCoordinate(lon) ? lon : DEFAULT_MAP_COORDINATES.lon;

    const mapContainerRef = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<mapboxgl.Map | null>(null);
    const [hasMapError, setHasMapError] = useState(false);

    useEffect(() => {
      if (!mapContainerRef.current || map !== null || hasMapError) {
        return;
      }

      if (!mapboxgl.accessToken) {
        setHasMapError(true);
        return;
      }

      let mapInstance: mapboxgl.Map | null = null;
      try {
        mapInstance = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: MAP_STYLE,
          center: [safeLat, safeLon],
          zoom: 5,
          maxZoom: 50,
          attributionControl: false,
        });

        mapInstance.addControl(
          new mapboxgl.NavigationControl({
            showCompass: false,
            visualizePitch: true,
            showZoom: true,
          }),
          "bottom-right"
        );

        mapInstance.on("error", (event) => {
          console.error("Mapbox error:", event?.error ?? event);
        });

        mapInstance.on("load", () => {
          if (onLoad && mapInstance) {
            try {
              onLoad(mapInstance);
            } catch (loadError) {
              console.error("Map onLoad handler failed:", loadError);
              setHasMapError(true);
            }
          }
        });
        setMap(mapInstance);
      } catch (error) {
        console.error("Failed to initialize Mapbox map:", error);
        if (mapInstance) {
          try {
            mapInstance.remove();
          } catch {
            // ignore cleanup errors
          }
        }
        setHasMapError(true);
      }
    }, [map, onLoad, safeLat, safeLon, hasMapError]);

    useImperativeHandle(
      ref,
      () => ({
        getMap: () => map,
        mapContainerRef: mapContainerRef.current,
      }),
      [map, mapContainerRef]
    );

    return (
      <div
        ref={mapContainerRef}
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          width: "100%",
          zIndex: 0,
        }}
      >
        {hasMapError && <MapErrorOverlay />}
        {props.children}
      </div>
    );
  }
);

MapComponent.displayName = "MapComponent";
export default React.memo(MapComponent);
